/**
 * Core field injection for logging.
 *
 * Provides utilities for creating core event fields and context providers.
 * Core fields are auto-injected on every log event.
 *
 * Environment-agnostic by design: this package reads no `import.meta.env`
 * or `process.env` itself. `service`/`environment`/`version` are supplied by
 * the caller, which is the app-owned composition layer
 * (apps/retells/src/logging/backend.ts) that actually knows how to read them.
 */

import { randomUUID } from "./random-uuid";
import type { CoreEventFields } from "./types";

/** What the caller supplies; everything else is generated here. */
export interface CoreFieldsInput {
  service: string;
  environment: string;
  version?: string;
}

/**
 * Create core fields for a log event.
 * These fields are auto-injected on every event.
 *
 * `eventId` goes through random-uuid.ts rather than `crypto.randomUUID`
 * directly: this runs once per event, and an origin without
 * `crypto.randomUUID` (see that file's measurement) would otherwise turn
 * every log call into a throw.
 *
 * @returns Core event fields with eventId, timestamp, and the caller-supplied
 *          service, environment, and version
 */
export function createCoreFields(input: CoreFieldsInput): CoreEventFields {
  return {
    eventId: randomUUID(),
    timestamp: Date.now(),
    environment: input.environment,
    service: input.service,
    version: input.version,
  };
}

/**
 * Interface for context providers.
 * Context providers supply session-specific information to the logger.
 */
export interface ContextProvider {
  /** Get the current session ID */
  getSessionId(): string | undefined;
  /** Get the current user ID (if authenticated) */
  getUserId(): string | undefined;
  /** Get the current reader ID (if the app has a reader identity) */
  getReaderId?(): string | undefined;
  /** Get the marketing lead ID this browser arrived as, if any */
  getLeadId?(): string | undefined;
}

/**
 * Create a no-op context provider.
 * Used when no context is available.
 *
 * @returns A context provider that returns undefined for all fields
 */
export function createEmptyContextProvider(): ContextProvider {
  return {
    getSessionId: () => undefined,
    getUserId: () => undefined,
    getReaderId: () => undefined,
    getLeadId: () => undefined,
  };
}
